import React, { useState, useEffect, useRef, useMemo } from "react";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { artworks, pressArticles, artistInfo } from "./data/artworks";
import { ChevronDown, ChevronLeft, ChevronRight, Menu, X, Instagram, ExternalLink, Mail, Phone, MapPin } from "lucide-react";
import HauteGalerie from "./components/HauteGalerie";
import "./App.css";

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Language Selector Component
const LanguageSelector = () => {
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
        data-testid="language-selector"
      >
        <span>{languages.find(l => l.code === currentLanguage)?.flag}</span>
        <span className="text-sm">{languages.find(l => l.code === currentLanguage)?.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-slate-900/95 backdrop-blur-xl rounded-lg border border-white/10 overflow-hidden z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`flex items-center gap-2 w-full px-4 py-3 hover:bg-white/10 transition-colors ${
                currentLanguage === lang.code ? 'bg-white/5' : ''
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span>{lang.flag}</span>
              <span className="text-sm whitespace-nowrap">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = ({ activeSection, scrollToSection }) => {
  const { t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'home', label: t('nav.home') },
    { id: 'biography', label: t('nav.biography') },
    { id: 'galleries', label: t('nav.galleries') },
    { id: 'shop', label: t('nav.shop') },
    { id: 'press', label: t('nav.press') },
    { id: 'contact', label: t('nav.contact') }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'bg-slate-950/90 backdrop-blur-xl py-4' : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a 
          href="#home" 
          onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
          className="text-2xl font-light tracking-[0.3em] text-white hover:text-blue-400 transition-colors"
          data-testid="logo"
        >
          GEORG ESCO
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`text-lg tracking-wider transition-colors font-light ${
                activeSection === item.id 
                  ? 'text-blue-400' 
                  : 'text-white/70 hover:text-white'
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <LanguageSelector />
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-white p-2"
          data-testid="mobile-menu-btn"
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu - OPAQUE */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-slate-950 border-t border-white/10 shadow-2xl">
          <div className="px-6 py-4 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  scrollToSection(item.id);
                  setIsMenuOpen(false);
                }}
                className={`block w-full text-left py-2 text-lg tracking-wider transition-colors font-light ${
                  activeSection === item.id 
                    ? 'text-blue-400' 
                    : 'text-white/70'
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="pt-4 border-t border-white/10">
              <LanguageSelector />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection }) => {
  const { t } = useLanguage();

  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background with original site blue gradient */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0a0e17 0%, #020617 30%, #020617 50%, #020617 70%, #0a0e17 100%)' }}>
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(30, 58, 95, 0.3) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: 'rgba(30, 58, 95, 0.2)' }} />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: 'rgba(15, 24, 41, 0.3)', animationDelay: '1s' }} />
        </div>
      </div>

      {/* Hero image - Ho Chi Minh Portrait */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://customer-assets.emergentagent.com/job_artist-site-maker/artifacts/mt74gt2r_1000014771.jpg"
          alt="Georg Esco - Portrait de Hô Chi Minh - Peinture contemporaine de prestige - Prix A Vietnam 2025 - Artiste peintre international"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extralight tracking-[0.4em] text-white mb-6 animate-fadeIn">
          GEORG
        </h1>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extralight tracking-[0.4em] text-blue-400/80 mb-12 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          ESCO
        </h2>
        <p className="text-2xl md:text-3xl text-white/60 tracking-[0.2em] font-light animate-fadeIn mb-8" style={{ animationDelay: '0.6s' }}>
          {t('hero.subtitle')}
        </p>
        <div className="mt-6 animate-fadeIn" style={{ animationDelay: '0.8s' }}>
          <span className="inline-block px-8 py-3 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-xl tracking-wider font-light">
            {t('hero.awardBadge')}
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <button 
        onClick={() => scrollToSection('biography')}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors animate-bounce"
        data-testid="scroll-indicator"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-base tracking-[0.3em]">{t('hero.scrollText')}</span>
          <ChevronDown className="w-6 h-6" />
        </div>
      </button>
    </section>
  );
};

// Biography Section
const BiographySection = () => {
  const { t } = useLanguage();

  return (
    <section 
      id="biography" 
      className="relative py-32"
      style={{ background: 'linear-gradient(180deg, #020617 0%, #020617 50%, #020617 100%)' }}
      data-testid="biography-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-5xl md:text-6xl font-extralight tracking-[0.3em] text-white mb-20 text-center">
          {t('bio.title')}
        </h2>

        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Childhood Photo */}
          <div className="relative group">
            <img 
              src="https://customer-assets.emergentagent.com/job_artist-site-maker/artifacts/0vkwixh4_1000000670.jpg"
              alt="Georg Esco - Enfance en Roumanie Transylvanie - Artiste peintre contemporain franco-roumain - Biographie officielle"
              className="relative rounded-lg shadow-lg w-full object-cover"
            />
            {/* Text below photo - NO BORDER */}
            <div className="mt-6 bg-slate-900/40 rounded-lg p-4">
              <p className="text-white/70 text-base font-light tracking-wider italic">
                {t('bio.photoCaption')}
              </p>
              <p className="text-lg font-medium tracking-wider mt-1" style={{ color: '#FFD700' }}>
                {t('bio.prizeA')}
              </p>
            </div>
          </div>

          {/* Biography Text */}
          <div className="space-y-6 text-white/70 leading-relaxed">
            <p className="text-xl">{t('bio.text1')}</p>
            <p className="text-xl">{t('bio.text2')}</p>
            <p className="text-xl">{t('bio.text3')}</p>
            <p className="text-xl">{t('bio.text4')}</p>
            <p className="text-xl text-white italic border-l-2 border-blue-400 pl-4">
              {t('bio.text5')}
            </p>
            <p className="text-xl">{t('bio.text6')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Index Section - Carrousel manuel des galeries (flèches + swipe)
const GalleryIndexSection = ({ onGallerySelect }) => {
  const { t } = useLanguage();
  
  const galleries = [
    { id: 'portraits', key: 'portraits' },
    { id: 'landscapes', key: 'landscapes' },
    { id: 'genesis', key: 'genesis' },
    { id: 'grids', key: 'grids' },
    { id: 'eroticism', key: 'eroticism' },
    { id: 'universe', key: 'universe' },
    { id: 'pollock', key: 'pollock' },
    { id: 'pollockCubism', key: 'pollockCubism' },
    { id: 'history', key: 'history' },
    { id: 'pantheon', key: 'pantheon' },
    { id: 'sacred', key: 'sacred' },
    { id: 'lines', key: 'lines' }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [stepPx, setStepPx] = useState(0);
  const [cardPx, setCardPx] = useState(0);
  const trackWrapperRef = useRef(null);
  const touchStartX = useRef(null);

  const gapPx = visibleCount === 1 ? 24 : 40; // mobile : 24, tablette/desktop : 40

  // Adapt visible count to viewport width
  useEffect(() => {
    const updateVisible = () => {
      const w = window.innerWidth;
      if (w < 640) setVisibleCount(1);
      else if (w < 1024) setVisibleCount(2);
      else setVisibleCount(3);
    };
    updateVisible();
    window.addEventListener('resize', updateVisible);
    return () => window.removeEventListener('resize', updateVisible);
  }, []);

  // Measure wrapper width and compute exact pixel sizes
  useEffect(() => {
    const measure = () => {
      if (!trackWrapperRef.current) return;
      const wrapperW = trackWrapperRef.current.clientWidth;
      const card = (wrapperW - (visibleCount - 1) * gapPx) / visibleCount;
      setCardPx(card);
      setStepPx(card + gapPx);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [visibleCount, gapPx]);

  const totalSlides = galleries.length;
  const maxIndex = Math.max(0, totalSlides - visibleCount);

  // Clamp current index when visibleCount changes
  useEffect(() => {
    if (currentIndex > maxIndex) setCurrentIndex(maxIndex);
  }, [maxIndex, currentIndex]);

  const goPrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };
  const goNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  return (
    <section 
      className="relative py-24"
      style={{ backgroundColor: '#020617' }}
    >
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-light tracking-[0.2em] text-white mb-16 text-center" style={{ fontFamily: 'Georgia, serif' }}>
          Collections
        </h2>

        <div className="relative">
          {/* Flèche gauche */}
          <button
            onClick={goPrev}
            aria-label="Collection précédente"
            data-testid="carousel-prev-btn"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-slate-900/70 hover:bg-slate-800/90 border border-white/10 hover:border-white/40 text-white/70 hover:text-white transition-all duration-300 -ml-2 md:-ml-4"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Flèche droite */}
          <button
            onClick={goNext}
            aria-label="Collection suivante"
            data-testid="carousel-next-btn"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-slate-900/70 hover:bg-slate-800/90 border border-white/10 hover:border-white/40 text-white/70 hover:text-white transition-all duration-300 -mr-2 md:-mr-4"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Piste défilante */}
          <div
            ref={trackWrapperRef}
            className="overflow-hidden mx-12 md:mx-16"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                gap: `${gapPx}px`,
                transform: `translateX(${-currentIndex * stepPx}px)`
              }}
            >
              {galleries.map((gallery) => {
                const firstImage = artworks[gallery.id]?.images?.[0]?.image;
                return (
                  <button
                    key={gallery.id}
                    onClick={() => onGallerySelect(gallery.id)}
                    className="flex-shrink-0 cursor-pointer group/item text-left"
                    style={{ width: cardPx ? `${cardPx}px` : `${100 / visibleCount}%` }}
                    data-testid={`collection-carousel-${gallery.id}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-slate-900/40 mb-5">
                      {firstImage ? (
                        <img
                          src={firstImage}
                          alt={t(`galleries.${gallery.key}`)}
                          className="w-full h-full object-contain transition-transform duration-700 group-hover/item:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-800/60" />
                      )}
                    </div>
                    <h3
                      className="text-base md:text-lg font-light tracking-[0.18em] text-white/90 text-center uppercase transition-colors group-hover/item:text-white min-h-[3.5rem] flex items-start justify-center px-2"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {t(`galleries.${gallery.key}`)}
                    </h3>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Card Component
const GalleryCard = ({ gallery, onClick }) => {
  const { t } = useLanguage();
  const galleryKeys = {
    portraits: 'portraits',
    landscapes: 'landscapes',
    genesis: 'genesis',
    grids: 'grids',
    eroticism: 'eroticism',
    universe: 'universe',
    pollock: 'pollock',
    pollockCubism: 'pollockCubism',
    history: 'history',
    pantheon: 'pantheon',
    sacred: 'sacred',
    lights: 'lights',
    lines: 'lines',
    diffraction: 'diffraction'
  };

  const key = galleryKeys[gallery.id];
  const title = t(`galleries.${key}`);
  const desc = t(`galleries.${key}Desc`);

  return (
    <button
      onClick={onClick}
      className="group relative aspect-square overflow-hidden rounded-lg border border-white/10 bg-slate-900/40"
      data-testid={`gallery-${gallery.id}`}
    >
      <img 
        src={gallery.images[0]?.image}
        alt={`${title} - Georg Esco - Collection exclusive d'art contemporain - Portraits de prestige et œuvres abstraites`}
        className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-gray-900/30 to-transparent group-hover:from-gray-900/80 transition-all" />
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <h3 className="text-xl font-light tracking-wider text-white mb-2 group-hover:text-white transition-colors">
          {title}
        </h3>
        <p className="text-sm text-white/90 line-clamp-2">
          {desc}
        </p>
        <div className="mt-4 flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-sm tracking-wider">{t('galleries.viewGallery')}</span>
          <ChevronDown className="w-4 h-4 -rotate-90" />
        </div>
      </div>
    </button>
  );
};

// Gallery Modal
const GalleryModal = ({ gallery, onClose, onImageSelect }) => {
  const { t, currentLanguage } = useLanguage();
  const containerRef = useRef(null);

  if (!gallery) return null;

  const galleryKeys = {
    portraits: 'portraits',
    landscapes: 'landscapes',
    genesis: 'genesis',
    grids: 'grids',
    eroticism: 'eroticism',
    universe: 'universe',
    pollock: 'pollock',
    pollockCubism: 'pollockCubism',
    history: 'history',
    pantheon: 'pantheon',
    sacred: 'sacred',
    lights: 'lights',
    lines: 'lines',
    diffraction: 'diffraction'
  };

  const key = galleryKeys[gallery.id];
  const title = t(`galleries.${key}`);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xl overflow-auto"
    >
      <button
        onClick={onClose}
        className="fixed top-6 right-6 text-white/70 hover:text-white z-[60] p-2 hover:bg-white/5 rounded-full transition-colors"
        data-testid="close-gallery"
      >
        <X className="w-8 h-8" />
      </button>

      <div className="max-w-7xl mx-auto px-6 py-24">
        {gallery.subtitle && (
          <p className="text-lg md:text-xl font-medium tracking-[0.2em] text-blue-300 mb-4 text-center uppercase drop-shadow-lg">
            {gallery.subtitle}
          </p>
        )}
        <h2 className="text-4xl font-extralight tracking-[0.3em] text-white mb-12 text-center">
          {title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative">
          {gallery.images.map((artwork) => (
            <button
              key={artwork.id}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onImageSelect(artwork);
              }}
              className="group relative aspect-square overflow-hidden rounded-lg bg-slate-900/40 transition-all hover:ring-2 hover:ring-blue-400/50"
            >
              <img 
                src={artwork.image}
                alt={`${typeof artwork.title === 'object' ? artwork.title[currentLanguage] || artwork.title.fr : artwork.title} - Georg Esco`}
                className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                <h4 className="text-blue-400 font-light text-lg">
                  {typeof artwork.title === 'object' ? artwork.title[currentLanguage] || artwork.title.fr : artwork.title}
                </h4>
                {artwork.dimensions && (
                  <p className="text-sm font-light mt-1 text-white">
                    {artwork.dimensions.replace('x', '×')}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Galleries Section
const GalleriesSection = ({ galleryToOpen, onGalleryClose }) => {
  const { t, currentLanguage } = useLanguage();
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryList = useMemo(() => Object.values(artworks), []);

  // Simple: If galleryToOpen is provided, open it
  useEffect(() => {
    if (galleryToOpen) {
      setSelectedGallery(galleryToOpen);
    }
  }, [galleryToOpen]);

  const handleClose = () => {
    setSelectedGallery(null);
    if (onGalleryClose) {
      onGalleryClose();
    }
  };

  return (
    <section 
      id="galleries" 
      className="relative py-32"
      style={{ backgroundColor: '#020617' }}
      data-testid="galleries-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-extralight tracking-[0.3em] text-white mb-16 text-center">
          {t('galleries.title')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {galleryList.map((gallery) => (
            <GalleryCard 
              key={gallery.id}
              gallery={gallery}
              onClick={() => setSelectedGallery(gallery)}
            />
          ))}
        </div>
      </div>

      {selectedGallery && (
        <GalleryModal 
          gallery={selectedGallery}
          onClose={handleClose}
          onImageSelect={(artwork) => setSelectedImage(artwork)}
        />
      )}

      {/* Image Overlay - OUTSIDE of gallery modal, truly fixed to viewport */}
      {selectedImage && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{
            zIndex: 100,
            backgroundColor: 'rgba(10, 14, 23, 0.95)',
            backdropFilter: 'blur(12px)'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="fixed top-4 right-4 text-white hover:text-white p-3 bg-slate-900/40 hover:bg-slate-900/40 rounded-full transition-colors shadow-xl"
            style={{ zIndex: 101 }}
          >
            <X className="w-7 h-7" />
          </button>
          
          <div 
            className="relative flex flex-col items-center max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Schema.org JSON-LD pour Google Lens */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "VisualArtwork",
                "name": typeof selectedImage.title === 'object' ? selectedImage.title[currentLanguage] || selectedImage.title.fr : selectedImage.title,
                "creator": {
                  "@type": "Person",
                  "name": "Georg Esco",
                  "url": "https://georg-esco.com"
                },
                "copyrightHolder": {
                  "@type": "Person",
                  "name": "Georg Esco"
                },
                "copyrightYear": selectedImage.year || "2024-2025",
                "image": selectedImage.image,
                "artMedium": "Peinture sur toile",
                "artform": "Peinture",
                "dateCreated": selectedImage.year || "2024-2025",
                "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/",
                "creditText": "© Georg Esco - All Rights Reserved"
              })}
            </script>
            <div className="bg-slate-900/40 backdrop-blur-sm rounded-xl p-3 md:p-5 shadow-2xl">
              <img 
                src={selectedImage.image}
                alt={`${typeof selectedImage.title === 'object' ? selectedImage.title[currentLanguage] || selectedImage.title.fr : selectedImage.title} - Peinture par Georg Esco - Œuvre originale unique`}
                className="rounded-lg shadow-2xl"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '60vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
            <div className="mt-4 text-center bg-slate-900/40 backdrop-blur-sm rounded-lg px-6 py-3 max-w-2xl shadow-xl">
              <h3 className="text-base md:text-xl text-blue-400 font-light">
                {typeof selectedImage.title === 'object' ? selectedImage.title[currentLanguage] || selectedImage.title.fr : selectedImage.title}
              </h3>
              {selectedImage.year && (
                <p className="text-xs md:text-sm mt-1 text-white">
                  {selectedImage.year}
                </p>
              )}
              {selectedImage.technique && selectedImage.dimensions && (
                <p className="text-sm md:text-base mt-2 text-white">
                  {(() => {
                    const technique = typeof selectedImage.technique === 'object' 
                      ? (selectedImage.technique[currentLanguage] || selectedImage.technique.fr)
                      : selectedImage.technique;
                    
                    return technique === 'Peinture sur toile' ? t('techniques.paintingOnCanvas') : 
                           technique === 'Huile et acrylique sur toile' ? t('techniques.oilAndAcrylicOnCanvas') : 
                           technique;
                  })()} - {selectedImage.dimensions.replace('x', '×').replace('cm', '').trim()} 
                  {selectedImage.dimensions.includes('cm') && (
                    <span className="text-white/70"> cm</span>
                  )}
                </p>
              )}
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-white/60 text-xs md:text-sm italic">
                  {t('copyright.paintingBy')}
                </p>
                <p className="text-white/80 text-xs mt-1 font-medium">
                  {t('copyright.uniqueWork')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Press Section
const PressSection = () => {
  const { t } = useLanguage();

  return (
    <section 
      id="press" 
      className="relative py-32"
      style={{ background: 'linear-gradient(180deg, #020617 0%, #020617 50%, #020617 100%)' }}
      data-testid="press-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-extralight tracking-[0.3em] mb-4 text-center" style={{ color: '#60A5FA' }}>
          PRESSE
        </h2>
        <p className="text-center mb-16 tracking-wider text-lg font-light" style={{ color: '#FFD700' }}>
          {t('press.subtitle')}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pressArticles.map((article) => (
            <div
              key={article.id}
              className="group relative overflow-hidden rounded-xl bg-slate-900/40 border border-white/5 hover:border-blue-400/30 transition-all duration-500"
              data-testid={`press-${article.id}`}
            >
              <div 
                className="aspect-square overflow-hidden"
                style={{ backgroundColor: article.darkBackground ? '#0a0e17' : 'transparent' }}
              >
                <img 
                  src={article.image}
                  alt={article.title}
                  className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
                    article.darkBackground ? 'object-contain p-4' : 'object-cover'
                  }`}
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-sm mb-3">
                  <span className="font-medium" style={{ color: '#FFD700' }}>{article.source}</span>
                  <span className="text-gray-500">•</span>
                  <span style={{ color: '#FFD700' }}>{article.date}</span>
                </div>
                <h3 className="text-lg text-blue-400 font-light leading-relaxed group-hover:text-blue-300 transition-colors mb-2">
                  {article.title}
                </h3>
                {article.subtitle && (
                  <p className="text-blue-300 italic text-base mt-1 mb-2">« {article.subtitle.replace(/[«»]/g, '')} »</p>
                )}
                {article.technique && (
                  <p className="text-blue-400/80 text-sm mt-2">{article.technique}</p>
                )}
                {article.dimensions && (
                  <p className="text-sm mt-1" style={{ color: '#FFD700' }}>{article.dimensions}</p>
                )}
                {article.description && (
                  <p className="text-white/60 text-sm mt-3 line-clamp-2">{article.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [emailError, setEmailError] = useState('');

  // Regex stricte de validation d'email (RFC 5322 simplifiée, exige un TLD valide)
  const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');

    const emailTrimmed = formData.email.trim();
    if (!EMAIL_REGEX.test(emailTrimmed)) {
      setEmailError("Format d'email invalide. Exemple attendu : prenom.nom@domaine.com");
      setStatus('');
      return;
    }

    setStatus('sending');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, email: emailTrimmed })
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section 
      id="contact" 
      className="relative py-32"
      style={{ backgroundColor: '#020617' }}
      data-testid="contact-section"
      data-section="contact"
    >
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl md:text-5xl font-extralight tracking-[0.3em] text-white mb-4 text-center">
          {t('contact.title')}
        </h2>
        <p className="text-center text-blue-400 mb-16 tracking-wider text-lg font-light">
          {t('contact.subtitle')}
        </p>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-blue-400 mt-1" />
              <div>
                <h4 className="text-white font-light mb-1">Email</h4>
                <a href={`mailto:${artistInfo.email}`} className="text-white/60 hover:text-blue-400 transition-colors">
                  {artistInfo.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-6 h-6 text-blue-400 mt-1" />
              <div>
                <h4 className="text-white font-light mb-1">Téléphone</h4>
                <a href={`tel:${artistInfo.phone}`} className="text-white/60 hover:text-blue-400 transition-colors">
                  {artistInfo.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-blue-400 mt-1" />
              <div>
                <h4 className="text-white font-light mb-1">Localisation</h4>
                <p className="text-white/60">Paris, France</p>
              </div>
            </div>

            {/* Social Links */}
            <div className="pt-8 border-t border-white/10">
              <h4 className="text-white font-light mb-4">{t('footer.followMe')}</h4>
              <div className="flex gap-4">
                <a
                  href={artistInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram className="w-5 h-5 text-pink-400" />
                  <span className="text-white/70 text-sm">{artistInfo.instagram}</span>
                </a>
                <a
                  href={artistInfo.tiktokUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="tiktok-link"
                >
                  <TikTokIcon className="w-5 h-5 text-white" />
                  <span className="text-white/70 text-sm">{artistInfo.tiktok}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="block text-blue-400/80 text-sm mb-2 font-light tracking-wider">{t('contact.name')}</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="block text-blue-400/80 text-sm mb-2 font-light tracking-wider">{t('contact.email')}</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (emailError) setEmailError('');
                }}
                aria-invalid={!!emailError}
                className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white focus:outline-none transition-colors ${emailError ? 'border-red-500 focus:border-red-500' : 'border-white/10 focus:border-blue-400'}`}
                data-testid="contact-email"
              />
              {emailError && (
                <p className="mt-2 text-sm font-light text-red-400" data-testid="contact-email-error">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className="block text-blue-400/80 text-sm mb-2 font-light tracking-wider">{t('contact.subject')}</label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <label className="block text-blue-400/80 text-sm mb-2 font-light tracking-wider">{t('contact.message')}</label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-blue-400 focus:outline-none transition-colors resize-none"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-blue-500/50 disabled:to-blue-600/50 text-white font-light tracking-wider rounded-lg transition-all duration-300 shadow-lg hover:shadow-blue-500/50"
              data-testid="contact-submit"
            >
              {status === 'sending' ? 'Envoi en cours...' : t('contact.send')}
            </button>
            {status === 'success' && (
              <p className="text-green-400 text-center">{t('contact.success')}</p>
            )}
            {status === 'error' && (
              <p className="text-red-400 text-center">{t('contact.error')}</p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="border-t border-white/5 py-8" style={{ backgroundColor: '#020617' }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Georg Esco - {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6">
            <a
              href={artistInfo.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-pink-400 transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={artistInfo.tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-white transition-colors"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
const MainApp = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [galleryToOpen, setGalleryToOpen] = useState(null);
  const sectionsRef = useRef({});

  const handleGallerySelect = (galleryId) => {
    // Find the gallery and pass it directly
    const gallery = Object.values(artworks).find(g => g.id === galleryId);
    if (gallery) {
      setGalleryToOpen(gallery);
      // Scroll to galleries section
      const element = document.getElementById('galleries');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleGalleryClose = () => {
    setGalleryToOpen(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      const sections = ['home', 'biography', 'galleries', 'shop', 'press', 'contact'];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0d1b2a' }}>
      <Navigation activeSection={activeSection} scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      <BiographySection />
      <GalleryIndexSection onGallerySelect={handleGallerySelect} />
      <GalleriesSection galleryToOpen={galleryToOpen} onGalleryClose={handleGalleryClose} />
      <div id="shop">
        <HauteGalerie />
      </div>
      <PressSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <MainApp />
    </LanguageProvider>
  );
}

export default App;
